import React, { useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from 'react-query';

// Components
import Layout from './components/Layout/Layout';
import Home from './pages/Home/Home';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import VerifyEmail from './pages/Auth/VerifyEmail';
import ResetPassword from './pages/Auth/ResetPassword';
import Profile from './pages/Profile/Profile';
import CourseChannels from './pages/CourseChannels/CourseChannels';
import CoursePage from './pages/CourseChannels/CoursePage';
import RoommateBoard from './pages/RoommateBoard/RoommateBoard';
import Search from './pages/Search/Search';
import Clubs from './pages/Clubs/Clubs';
import Professors from './pages/Professors/Professors';
import ProfessorDetail from './pages/Professors/ProfessorDetail';
import Events from './pages/Events/Events';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import Inbox from './pages/Messages/Inbox';
import { AuthProvider } from './contexts/AuthContext';
import Terms from './pages/Legal/Terms';
import Privacy from './pages/Legal/Privacy';
import Cookies from './pages/Legal/Cookies';
import LegalAcceptance from './pages/Legal/LegalAcceptance';
import Settings from './pages/Settings/Settings';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import { createAppTheme, type AppColorMode } from './theme';
import { ColorModeContext } from './contexts/ColorModeContext';

const COLOR_MODE_KEY = 'dragonsframe_color_mode';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const [mode, setMode] = useState<AppColorMode>(() => {
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem(COLOR_MODE_KEY) : null;
    return saved === 'dark' || saved === 'light' ? saved : 'light';
  });

  const colorMode = useMemo(
    () => ({
      mode,
      toggle: () =>
        setMode((prev) => {
          const next: AppColorMode = prev === 'light' ? 'dark' : 'light';
          try { window.localStorage.setItem(COLOR_MODE_KEY, next); } catch {}
          return next;
        }),
    }),
    [mode]
  );

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <QueryClientProvider client={queryClient}>
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthProvider>
            <CookieConsentProvider>
              <Router>
                <Layout>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/cookies" element={<Cookies />} />
                    <Route path="/" element={
                      <ProtectedRoute>
                        <Home />
                      </ProtectedRoute>
                    } />
                    <Route path="/profile" element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    } />
                    <Route path="/profile/:id" element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    } />
                    <Route path="/courses" element={
                      <ProtectedRoute>
                        <CourseChannels />
                      </ProtectedRoute>
                    } />
                    <Route path="/courses/:id" element={
                      <ProtectedRoute>
                        <CoursePage />
                      </ProtectedRoute>
                    } />
                    <Route path="/roommates" element={
                      <ProtectedRoute>
                        <RoommateBoard />
                      </ProtectedRoute>
                    } />
                    <Route path="/search" element={
                      <ProtectedRoute>
                        <Search />
                      </ProtectedRoute>
                    } />
                    <Route path="/clubs" element={
                      <ProtectedRoute>
                        <Clubs />
                      </ProtectedRoute>
                    } />
                    <Route path="/professors" element={
                      <ProtectedRoute>
                        <Professors />
                      </ProtectedRoute>
                    } />
                    <Route path="/professors/:id" element={
                      <ProtectedRoute>
                        <ProfessorDetail />
                      </ProtectedRoute>
                    } />
                    <Route path="/events" element={
                      <ProtectedRoute>
                        <Events />
                      </ProtectedRoute>
                    } />
                    <Route path="/messages" element={
                      <ProtectedRoute>
                        <Inbox />
                      </ProtectedRoute>
                    } />
                    <Route path="/messages/:peerId" element={
                      <ProtectedRoute>
                        <Inbox />
                      </ProtectedRoute>
                    } />
                    <Route path="/settings" element={
                      <ProtectedRoute>
                        <Settings />
                      </ProtectedRoute>
                    } />
                    <Route path="/legal/acceptance" element={
                      <ProtectedRoute>
                        <LegalAcceptance />
                      </ProtectedRoute>
                    } />
                  </Routes>
                </Layout>
              </Router>
            </CookieConsentProvider>
          </AuthProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </QueryClientProvider>
  );
}

export default App;
